import React, { useState, useEffect } from 'react'
import { parametroService, Parametro } from '../services/parametro'

const ParametrosListPage: React.FC = () => {
  const [parametros, setParametros] = useState<Parametro[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTipo, setSelectedTipo] = useState('')
  const [tipos, setTipos] = useState<string[]>([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingParametro, setEditingParametro] = useState<Parametro | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null)

  // Estados do formulário
  const [formData, setFormData] = useState({
    tipo: '',
    chave: '',
    valor: '',
    descricao: '',
    config_path: ''
  })

  // Carregar dados iniciais
  useEffect(() => {
    loadParametros()
    loadTipos()
  }, [])

  const loadParametros = async () => {
    try {
      setLoading(true)
      const response = await parametroService.getParametros({ paginated: false })
      if (response.success && Array.isArray(response.data)) {
        setParametros(response.data)
      }
    } catch (err) {
      setError('Erro ao carregar parâmetros')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const loadTipos = async () => {
    try {
      const response = await parametroService.getTipos()
      if (response.success) {
        setTipos(response.data)
      }
    } catch (err) {
      console.error('Erro ao carregar tipos:', err)
    }
  }

  const handleSearch = async () => {
    try {
      setLoading(true)
      let response
      
      if (searchTerm) {
        response = await parametroService.getParametros({ search: searchTerm, paginated: false })
      } else if (selectedTipo) {
        response = await parametroService.getParametros({ tipo: selectedTipo, paginated: false })
      } else {
        response = await parametroService.getParametros({ paginated: false })
      }
      
      if (response.success && Array.isArray(response.data)) {
        setParametros(response.data)
      }
    } catch (err) {
      setError('Erro ao buscar parâmetros')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const response = await parametroService.createParametro(formData)
      if (response.success) {
        setShowCreateModal(false)
        setFormData({ tipo: '', chave: '', valor: '', descricao: '', config_path: '' })
        loadParametros()
        loadTipos()
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao criar parâmetro')
    }
  }

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingParametro) return

    try {
      const response = await parametroService.updateParametro(editingParametro.id_parametro, formData)
      if (response.success) {
        setShowEditModal(false)
        setEditingParametro(null)
        setFormData({ tipo: '', chave: '', valor: '', descricao: '', config_path: '' })
        loadParametros()
        loadTipos()
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar parâmetro')
    }
  }

  const handleDelete = async (id: number) => {
    try {
      const response = await parametroService.deleteParametro(id)
      if (response.success) {
        setDeleteConfirm(null)
        loadParametros()
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao deletar parâmetro')
    }
  }

  const openEditModal = (parametro: Parametro) => {
    setEditingParametro(parametro)
    setFormData({
      tipo: parametro.tipo || '',
      chave: parametro.chave,
      valor: parametro.valor,
      descricao: parametro.descricao || '',
      config_path: parametro.config_path || ''
    })
    setShowEditModal(true)
  }

  const initDefaults = async () => {
    try {
      const response = await parametroService.initDefaults()
      if (response.success) {
        loadParametros()
        loadTipos()
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao inicializar parâmetros padrão')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Gerenciar Parâmetros</h1>
          <div className="flex gap-2">
            <button
              onClick={initDefaults}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Inicializar Padrões
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              + Novo Parâmetro
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar por chave/descrição
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Digite para buscar..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrar por tipo
            </label>
            <select
              value={selectedTipo}
              onChange={(e) => setSelectedTipo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos os tipos</option>
              {tipos.map(tipo => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Buscar
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
            <button 
              onClick={() => setError(null)}
              className="float-right text-red-700 hover:text-red-900"
            >
              ×
            </button>
          </div>
        )}

        {/* Tabela */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Chave
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {parametros.map((parametro) => (
                <tr key={parametro.id_parametro} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {parametro.chave}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="max-w-xs truncate block" title={parametro.valor}>
                      {parametro.valor}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {parametro.tipo && (
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        {parametro.tipo}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <span className="max-w-xs truncate block" title={parametro.descricao}>
                      {parametro.descricao}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openEditModal(parametro)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(parametro.id_parametro)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {parametros.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Nenhum parâmetro encontrado
            </div>
          )}
        </div>
      </div>

      {/* Modal Criar */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Novo Parâmetro</h3>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tipo</label>
                  <input
                    type="text"
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ex: email, app, config"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Chave *</label>
                  <input
                    type="text"
                    value={formData.chave}
                    onChange={(e) => setFormData({ ...formData, chave: e.target.value })}
                    required
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ex: email_destinatario"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Valor *</label>
                  <input
                    type="text"
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                    required
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ex: admin@unifap.br"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Descrição</label>
                  <textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Descrição do parâmetro"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Config Path</label>
                  <input
                    type="text"
                    value={formData.config_path}
                    onChange={(e) => setFormData({ ...formData, config_path: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ex: email.vistoria.destinatario"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  Criar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      {showEditModal && editingParametro && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Editar Parâmetro</h3>
            <form onSubmit={handleEdit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tipo</label>
                  <input
                    type="text"
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Chave *</label>
                  <input
                    type="text"
                    value={formData.chave}
                    onChange={(e) => setFormData({ ...formData, chave: e.target.value })}
                    required
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Valor *</label>
                  <input
                    type="text"
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                    required
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Descrição</label>
                  <textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Config Path</label>
                  <input
                    type="text"
                    value={formData.config_path}
                    onChange={(e) => setFormData({ ...formData, config_path: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Confirmação Delete */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirmar Exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir este parâmetro? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ParametrosListPage
